import { useEffect, useMemo, useState } from "react";
import { CurrencyIcon } from "@/brand";
import { cn } from "@/lib/cn";
import { monogramGlyph } from "./resolve";
import { resolveRef, type CurrencyRef } from "./format";
import { usePlatformCurrencySettings, useCurrencySettings } from "./CurrencySettingsProvider";
import { imageSourcesFor } from "./brokerOverlay";
import type { CurrencyPresentationSettings } from "./types";

// CurrencyMark — a currency's glyph, and nothing else. Never a value.
//
// Sizing follows the same convention as the brand marks: the call site passes
// className/style and the mark fills that box (default ~0.92em, so it sits on
// the text baseline beside a figure).
//
// Three sources, one guarantee — SOMETHING drawable always renders:
//   image    → operator artwork; on a load error it swaps to the monogram, so
//              a stale URL degrades to a provisional badge instead of the
//              browser's broken-image box next to a real balance. GOLD tries
//              the platform's images first (SM-978): a broker's image that
//              fails falls back to the registry's GOLD image, then the
//              bundled coin, and only then to the monogram.
//   brand    → the active brand's currency glyph. Selectable, but no currency's
//              seeded default any more: it was GOLD's, which nailed the ⚡ mark
//              to the one currency an operator most needs to be able to re-skin.
//   monogram → a generated badge, deterministic from the resolved symbol.
//
// Operator configuration comes from the provider unless the caller passes its
// own `settings`. It used to be caller-threaded only, so a mark rendered by a
// component that had not remembered to call useCurrencySettings silently fell
// back to the frozen seed.

export interface CurrencyMarkProps {
  currency: CurrencyRef;
  settings?: CurrencyPresentationSettings;
  /**
   * Edge length in px. Defaults to 0.92em so the mark tracks the font size.
   * Dimensions are applied as inline style, so this is the sizing knob —
   * `className` is for colour and spacing, and an `h-4 w-4` there would lose
   * to the inline box.
   */
  size?: number;
  className?: string;
  /**
   * Decorative. Set when an ancestor already names the currency in text — as
   * <CurrencyAmount> does via its aria-label — so a screen reader does not hear
   * the unit twice.
   */
  "aria-hidden"?: boolean;
  /** Overrides the default accessible name (the currency's display name). */
  "aria-label"?: string;
}

export function CurrencyMark({
  currency,
  settings,
  size,
  className,
  "aria-hidden": ariaHidden = false,
  "aria-label": ariaLabel,
}: CurrencyMarkProps) {
  const contextSettings = useCurrencySettings();
  const platformSettings = usePlatformCurrencySettings();
  const presentation = resolveRef(currency, settings ?? contextSettings);
  const { icon, color, symbol, displayName } = presentation;
  const sources = useMemo(() => imageSourcesFor(presentation, platformSettings), [presentation, platformSettings]);
  const sourcesKey = sources.join("\n");
  // How many of `sources` failed to load, in order.
  const [failed, setFailed] = useState(0);

  // New sources deserve a fresh attempt — otherwise one bad URL would poison
  // every later currency rendered through the same element position.
  useEffect(() => setFailed(0), [sourcesKey]);
  const src = sources[failed] ?? null;

  const box = size != null ? { width: size, height: size } : { width: "0.92em", height: "0.92em" };
  const label = ariaLabel ?? displayName;
  const a11y = ariaHidden ? ({ "aria-hidden": true } as const) : ({ role: "img", "aria-label": label } as const);

  if (icon.kind === "image" && src) {
    const attempt = failed;
    return (
      <img
        key={src}
        src={src}
        // alt is empty when decorative: an aria-hidden element with alt text is
        // a contradiction, and the amount beside it already carries the name.
        alt={ariaHidden ? "" : attempt === 0 ? (icon.alt ?? label) : label}
        {...(ariaHidden ? { "aria-hidden": true } : {})}
        onError={() => setFailed((n) => (n === attempt ? attempt + 1 : n))}
        className={cn("inline-block shrink-0 object-contain", className)}
        style={box}
      />
    );
  }

  if (icon.kind === "brand") {
    // Inherits currentColor — the currency's configured `color` deliberately
    // does NOT tint this one. A brand mark is the brand's artwork (src/brand),
    // and letting a currency setting repaint it would put operator
    // configuration on top of the white-label contract. Call sites keep tinting
    // it with a brand token exactly as they always have (`text-warning`).
    // `color` still renders wherever the currency owns the pixels: the monogram
    // badge below, and any accent a call site draws from `presentation.color`.
    return <CurrencyIcon {...a11y} className={cn("shrink-0", className)} style={box} />;
  }

  // The monogram is a PILL, not a square: two characters need width, and
  // squeezing them into an icon-sized box renders them too small to read at the
  // 0.92em default. Height still tracks `size` so the badge sits on the same
  // baseline as artwork and the brand glyph; width grows with the glyph.
  const glyph = monogramGlyph(symbol);
  return (
    <span
      {...a11y}
      className={cn(
        "inline-flex shrink-0 items-center justify-center rounded-[0.35em] px-[0.35em] font-bold leading-none tracking-tight",
        className,
      )}
      style={{
        height: box.height,
        minWidth: box.width,
        backgroundColor: color,
        // White on a mid-tone badge. The glyph is a shape here, not copy — the
        // accessible name carries the currency for anyone who cannot see it.
        color: "#fff",
        fontSize: size != null ? Math.max(9, Math.round(size * 0.62)) : "0.58em",
      }}
    >
      {glyph}
    </span>
  );
}
